import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { Category, CategoryCreate } from '../types';
import {
  useCategories,
  useDeletedCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  useRestoreCategory,
} from '../hooks/queries/useCategories';
import AlertDialog from './AlertDialog';
import useAlert from '../hooks/useAlert';
import { getModuleTableTheme } from '../theme/tableTheme';
import CustomTablePagination from './CustomTablePagination';
import PermissionButton from './PermissionButton';
import ColumnFilter from './ColumnFilter';
import { useColumnFilters } from '../hooks/useColumnFilters';

const CONFIG_COLOR = '#1976D2';

const CATEGORY_FILTER_SCHEMA = {
  name:       { type: 'text', param: 'name' },
  created_at: { type: 'dateRange', paramFrom: 'created_from', paramTo: 'created_to' },
} as const;

const CategoryManagement: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({ name: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { filters, update, reset, hasActive } = useColumnFilters(CATEGORY_FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });

  // Cached + live queries: the list auto-refreshes when any user changes a
  // category (WebSocket invalidation), and navigating back is instant from cache.
  const categoriesQuery = useCategories();
  const deletedQuery = useDeletedCategories(tabValue === 1);
  const categories = categoriesQuery.data ?? [];
  const deletedCategories = deletedQuery.data ?? [];
  const loading = tabValue === 0 ? categoriesQuery.isLoading : deletedQuery.isLoading;

  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();
  const restoreCategory = useRestoreCategory();
  const formLoading = createCategory.isLoading || updateCategory.isLoading;

  const handleRestore = async (category: Category) => {
    try {
      await restoreCategory.mutateAsync(category);
      showSuccess('Catégorie restaurée avec succès');
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Erreur lors de la restauration');
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;

    try {
      if (selectedCategory) {
        await updateCategory.mutateAsync({ id: selectedCategory.id, data: { name: formData.name } });
        showSuccess('Catégorie modifiée avec succès');
      } else {
        await createCategory.mutateAsync({ name: formData.name });
        showSuccess('Catégorie créée avec succès');
      }

      setIsFormOpen(false);
      setSelectedCategory(null);
      setFormData({ name: '' });
    } catch (err: any) {
      showError(
        err.response?.data?.detail ||
        'Erreur lors de la sauvegarde de la catégorie'
      );
    }
  };

  const handleDelete = async (category: Category) => {
    try {
      await deleteCategory.mutateAsync(category);
      showSuccess('Catégorie supprimée avec succès');
    } catch (err: any) {
      showError(
        err.response?.data?.detail ||
        'Erreur lors de la suppression de la catégorie'
      );
    }
  };

  const openForm = (category?: Category) => {
    setSelectedCategory(category || null);
    setFormData({ name: category?.name || '' });
    setIsFormOpen(true);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredCategories = useMemo(
    () =>
      categories.filter((c) => {
        if (filters.name && !c.name.toLowerCase().includes(filters.name.toLowerCase())) return false;
        if (filters.created_at.from && new Date(c.created_at) < new Date(filters.created_at.from)) return false;
        if (filters.created_at.to && new Date(c.created_at) > new Date(`${filters.created_at.to}T23:59:59`)) return false;
        return true;
      }),
    [categories, filters]
  );

  // Get current page data based on tab
  const currentData = tabValue === 0 ? filteredCategories : deletedCategories;
  const paginatedCategories = currentData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box p={3} sx={{ width: '100%', maxWidth: 'none' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Gestion des Catégories
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tabValue === 0 && hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary' }}
            >
              Effacer les filtres
            </Button>
          )}
          {tabValue === 0 && (
          <PermissionButton
            moduleName="Config"
            operation="add"
            miniModuleName="Categories"
            variant="contained"
            onClick={() => openForm()}
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#1976D2',
              '&:hover': {
                backgroundColor: '#1565C0',
              }
            }}
          >
            Nouvelle Catégorie
          </PermissionButton>
          )}
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Catégories Actives" />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
      <>
      <TableContainer component={Paper} sx={getModuleTableTheme('configuration')}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Nom
                  <ColumnFilter type="text" value={filters.name} onChange={(v) => update('name', v)} label="nom" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Date de Création
                  <ColumnFilter type="dateRange" value={filters.created_at} onChange={(v) => update('created_at', v)} label="date de création" color={CONFIG_COLOR} />
                </Box>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Chargement...
                </TableCell>
              </TableRow>
            ) : filteredCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  {hasActive() ? 'Aucune catégorie ne correspond aux filtres' : 'Aucune catégorie trouvée'}
                </TableCell>
              </TableRow>
            ) : (
              paginatedCategories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>{category.name}</TableCell>
                  <TableCell>
                    {new Date(category.created_at).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell align="center">
                    <PermissionButton
                      moduleName="Config"
                      operation="edit"
                      miniModuleName="Categories"
                      component="IconButton"
                      onClick={() => openForm(category)}
                    >
                      <EditIcon />
                    </PermissionButton>
                    <PermissionButton
                      moduleName="Config"
                      operation="delete"
                      miniModuleName="Categories"
                      component="IconButton"
                      onClick={() => {
                        showConfirm(
                          `Êtes-vous sûr de vouloir supprimer la catégorie "${category.name}" ?`,
                          () => handleDelete(category),
                          {
                            title: 'Confirmer la suppression',
                            confirmText: 'Supprimer',
                            cancelText: 'Annuler'
                          }
                        );
                      }}
                      color="error"
                    >
                      <DeleteIcon />
                    </PermissionButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={filteredCategories.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      </>
      )}

      {tabValue === 1 && (
      <>
      <TableContainer component={Paper} sx={getModuleTableTheme('configuration')}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nom</TableCell>
              <TableCell>Date de Suppression</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Chargement...
                </TableCell>
              </TableRow>
            ) : deletedCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Aucune catégorie supprimée
                </TableCell>
              </TableRow>
            ) : (
              paginatedCategories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>{category.name}</TableCell>
                  <TableCell>
                    {category.deleted_at ? new Date(category.deleted_at).toLocaleDateString('fr-FR') : '-'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => {
                        showConfirm(
                          `Êtes-vous sûr de vouloir restaurer la catégorie "${category.name}" ?`,
                          () => handleRestore(category),
                          {
                            title: 'Confirmer la restauration',
                            confirmText: 'Restaurer',
                            cancelText: 'Annuler'
                          }
                        );
                      }}
                      color="primary"
                      title="Restaurer"
                    >
                      <RestoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={deletedCategories.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      </>
      )}

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)}>
        <DialogTitle>
          {selectedCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom de la catégorie"
            fullWidth
            variant="filled"
            value={formData.name}
            onChange={(e) => setFormData({ name: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFormOpen(false)} disabled={formLoading}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={formLoading || !formData.name.trim()}
            sx={{ 
              backgroundColor: '#4CAF50',
              '&:hover': {
                backgroundColor: '#388E3C',
              }
            }}
          >
            {formLoading ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default CategoryManagement;